import { Calendar, Users, ChevronRight } from 'lucide-react'

interface StickyBookingBarProps {
  checkIn: string
  checkOut: string
  nights: number
  guests: string
  rent: number
  total: number
  onBookClick: () => void
}

const StickyBookingBar = ({
  checkIn,
  checkOut,
  nights,
  guests,
  rent,
  total,
  onBookClick
}: StickyBookingBarProps) => {
  const hasDates = checkIn && checkOut && nights > 0

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl safe-area-bottom">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2.5 sm:py-4">
        {/* Mobile layout: price/info on left, button on right */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            {hasDates ? (
              <>
                {/* Mobile: compact summary */}
                <div className="sm:hidden">
                  <p className="text-base font-bold text-gray-900">₹{total > 0 ? total : rent}</p>
                  <p className="text-[11px] text-gray-500 truncate">
                    {new Date(checkIn).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {new Date(checkOut).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {' · '}{nights} night{nights !== 1 ? 's' : ''}
                    {guests && guests !== 'all' ? ` · ${guests} guest${Number(guests) !== 1 ? 's' : ''}` : ''}
                  </p>
                </div>
                {/* Tablet+ : expanded layout */}
                <div className="hidden sm:flex items-center gap-4 md:gap-6">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Calendar className="h-5 w-5 text-primary-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500">Check-in / Check-out</p>
                      <p className="text-sm font-semibold">
                        {new Date(checkIn).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(checkOut).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Users className="h-5 w-5 text-primary-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500">Guests</p>
                      <p className="text-sm font-semibold">{guests || 'Not selected'}</p>
                    </div>
                  </div>
                  <div className="hidden md:block border-l border-gray-300 pl-6">
                    <p className="text-xs text-gray-500">Total Stay</p>
                    <p className="text-sm font-semibold">{nights} night{nights !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="hidden lg:block border-l border-gray-300 pl-6">
                    <p className="text-xs text-gray-500">Total Price</p>
                    <p className="text-lg font-bold text-primary-600">₹{total}</p>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-gray-600">
                <p className="text-xs sm:text-sm font-medium">₹{rent} <span className="font-normal text-gray-400">/ night</span></p>
                <p className="text-[11px] sm:text-xs text-gray-400">Select dates to book</p>
              </div>
            )}
          </div>

          {/* Book Button */}
          <button
            onClick={onBookClick}
            className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg sm:rounded-xl font-bold text-sm sm:text-base hover:from-primary-600 hover:to-primary-700 transition-all duration-200 hover:shadow-lg active:scale-95 whitespace-nowrap flex-shrink-0"
          >
            <span>Book Now</span>
            <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default StickyBookingBar

